import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker - using local bundled worker instead of CDN for reliability
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export interface RenderedPage {
	pageNumber: number;
	canvas: HTMLCanvasElement;
	imageData: ImageData;
	width: number;
	height: number;
}

/**
 * Renders a PDF to canvases, one per page
 * Returns an array of canvas elements and their ImageData
 */
export const renderPdfToCanvases = async (pdfBlob: Blob, scale: number = 2): Promise<RenderedPage[]> => {
	const arrayBuffer = await pdfBlob.arrayBuffer();
	const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

	try {
		const renderedPages: RenderedPage[] = [];

		for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
			const page = await pdf.getPage(pageNum);
			const viewport = page.getViewport({ scale });

			// Create canvas
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');

			if (!context) {
				throw new Error('Failed to get 2D context');
			}

			canvas.width = viewport.width;
			canvas.height = viewport.height;

			// Render PDF page to canvas
			await page.render({
				canvasContext: context,
				viewport: viewport,
			}).promise;

			// Get ImageData for pixel comparison
			const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

			renderedPages.push({
				pageNumber: pageNum,
				canvas,
				imageData,
				width: canvas.width,
				height: canvas.height,
			});
		}

		return renderedPages;
	} finally {
		// Clean up PDF document and its worker to prevent memory leaks
		pdf.destroy();
	}
};
